import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Sheet } from './Sheet';
import { DateTimeField, dtFromMs, resolveDateTime, type DateTimeValue } from './DateTimeField';
import { TYPE_META, SyncBadge } from './eventMeta';
import { IconMinus, IconPlus, IconTrash } from './Icons';
import { useToast } from './Toast';
import {
  ActiveSleepError,
  ValidationError,
  createEvent,
  deleteEvent,
  findOverlappingSleeps,
  restoreEvent,
  updateEvent,
} from '@/db/repo';
import {
  type DiaperPayload,
  type DiaperStatus,
  type EventType,
  type FormulaPayload,
  type LocalEvent,
  type SleepKind,
  type SleepPayload,
} from '@/lib/schemas';
import { formatDuration, formatTime } from '@/lib/time';
import { useApp } from '@/app/AppContext';

export interface EditorTarget {
  type: EventType;
  event?: LocalEvent;
}

interface Props {
  target: EditorTarget | null;
  babyId: string;
  tz: string;
  onClose: () => void;
  /** Most recent formula volume, used as default for new feeds. */
  lastAmountMl?: number;
}

const QUICK_ML = [60, 90, 120, 150, 180];

export function EventEditor({ target, babyId, tz, onClose, lastAmountMl }: Props) {
  const toast = useToast();
  const { formulaWarningMl } = useApp();
  const editing = target?.event;
  const type = target?.type ?? 'formula';
  const meta = TYPE_META[type];

  const [start, setStart] = useState<DateTimeValue>(() => dtFromMs(Date.now(), tz));
  const [end, setEnd] = useState<DateTimeValue>(() => dtFromMs(Date.now(), tz));
  const [running, setRunning] = useState(false);
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState<DiaperStatus>('wet');
  const [kind, setKind] = useState<SleepKind | null>(null);
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [overlapAck, setOverlapAck] = useState(false);
  const [overlaps, setOverlaps] = useState<LocalEvent[]>([]);
  const [overlapError, setOverlapError] = useState(false);
  const [checkingOverlap, setCheckingOverlap] = useState(false);
  const [busy, setBusy] = useState(false);

  // Reset form whenever a new target opens
  useEffect(() => {
    if (!target) return;
    const now = Date.now();
    const e = target.event;
    setError(null);
    setOverlapAck(false);
    setOverlaps([]);
    setOverlapError(false);
    setStart(dtFromMs(e?.started_ms ?? (target.type === 'sleep' ? now - 30 * 60_000 : now), tz));
    setEnd(dtFromMs(e?.ended_ms ?? now, tz));
    setRunning(!!e && e.type === 'sleep' && e.ended_ms === null);
    setNote((e?.payload as { note?: string } | undefined)?.note ?? '');
    if (target.type === 'formula') {
      const a = e ? (e.payload as FormulaPayload).amount_ml : lastAmountMl;
      setAmount(a != null ? String(a) : '');
    }
    if (target.type === 'diaper') setStatus(e ? (e.payload as DiaperPayload).status : 'wet');
    if (target.type === 'sleep') setKind(e ? (e.payload as SleepPayload).sleep_kind : null);
  }, [target, tz, lastAmountMl]);

  const startRes = resolveDateTime(start, tz);
  const endRes = resolveDateTime(end, tz);
  const amountNum = amount.trim() === '' ? NaN : Number(amount);

  // Live overlap check for sleeps
  useEffect(() => {
    if (type !== 'sleep' || !target || !startRes.ok) return;
    const endMs = running ? null : endRes.ok ? endRes.ms : null;
    let cancelled = false;
    setCheckingOverlap(true);
    setOverlapAck(false);
    setOverlapError(false);
    findOverlappingSleeps(babyId, startRes.ms, endMs, editing?.id).then((o) => {
      if (!cancelled) setOverlaps(o);
    }).catch(() => { if (!cancelled) setOverlapError(true); })
      .finally(() => { if (!cancelled) setCheckingOverlap(false); });
    return () => {
      cancelled = true;
    };
  }, [type, target, babyId, editing?.id, running, startRes.ok, endRes.ok, startRes.ok && startRes.ms, endRes.ok && endRes.ms]);

  const durationMs = useMemo(
    () => (type === 'sleep' && !running && startRes.ok && endRes.ok ? endRes.ms - startRes.ms : null),
    [type, running, startRes, endRes],
  );

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    if (busy) return;
    setError(null);
    if (!startRes.ok) return setError(startRes.error);
    let payload: Record<string, unknown>;
    let ended_ms: number | null = null;

    if (type === 'formula') {
      if (!Number.isFinite(amountNum) || amountNum < 0) return setError('Enter a volume of 0 mL or more');
      payload = { amount_ml: amountNum, note: note.trim() };
    } else if (type === 'diaper') {
      payload = { status, note: note.trim() };
    } else {
      if (checkingOverlap || overlapError) return setError('Could not verify sleep overlaps. Reopen the form and try again.');
      if (!running) {
        if (!endRes.ok) return setError(endRes.error);
        if (endRes.ms < startRes.ms) return setError('Sleep end must be after the start');
        ended_ms = endRes.ms;
      }
      if (overlaps.length > 0 && !overlapAck) return setError('This overlaps another sleep. Confirm below to save anyway.');
      payload = { sleep_kind: kind, note: note.trim() };
    }

    setBusy(true);
    try {
      if (editing) {
        await updateEvent(editing.id, { started_ms: startRes.ms, ended_ms: type === 'sleep' ? ended_ms : null, payload });
        toast.show(`${meta.label} updated`, { tone: 'ok' });
      } else {
        await createEvent({ baby_id: babyId, type, started_ms: startRes.ms, ended_ms, payload: payload as never });
        toast.show(`${meta.label} saved`, { tone: 'ok' });
      }
      onClose();
    } catch (err) {
      if (err instanceof ActiveSleepError)
        setError(`A sleep is already running (started ${formatTime(err.active.started_ms, tz)}). End it first.`);
      else if (err instanceof ValidationError) setError(err.message);
      else setError('Could not save. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!editing || busy) return;
    setBusy(true);
    setError(null);
    try {
      await deleteEvent(editing.id);
      onClose();
      toast.show(`${meta.label} deleted`, { action: { label: 'Undo', run: () => {
        void restoreEvent(editing.id).catch((err: unknown) => toast.show(err instanceof ActiveSleepError ? err.message : 'Could not restore the event. Please try again.', { tone: 'error', ms: 8000 }));
      } } });
    } catch {
      setError('Could not delete. Your event is still saved. Please try again.');
    } finally { setBusy(false); }
  }

  const title = `${editing ? 'Edit' : 'Add'} ${meta.label.toLowerCase()}`;

  return (
    <Sheet
      open={!!target}
      title={title}
      onClose={onClose}
      accent={meta.color}
      busy={busy}
      footer={
        <div className="flex gap-2">
          {editing && (
            <button type="button" className="btn-danger" disabled={busy} onClick={remove} aria-label={`Delete ${meta.label}`}>
              <IconTrash size={18} />
            </button>
          )}
          <button type="submit" form="event-form" className="btn-primary flex-1" disabled={busy || (type === 'sleep' && (checkingOverlap || overlapError))} id="event-save">
            {editing ? 'Save changes' : `Save ${meta.label.toLowerCase()}`}
          </button>
        </div>
      }
    >
      <form id="event-form" onSubmit={submit} className="space-y-5 pt-1" noValidate>
        {type === 'formula' && (
          <div className="space-y-2">
            <label htmlFor="amount" className="label">
              Volume (mL)
            </label>
            <div className="flex items-center gap-2">
              <button
                type="button"
                className="btn-ghost !w-12 !px-0"
                aria-label="Decrease by 10 mL"
                onClick={() => setAmount(String(Math.max(0, (Number.isFinite(amountNum) ? amountNum : 0) - 10)))}
              >
                <IconMinus size={18} />
              </button>
              <input
                id="amount"
                data-autofocus
                inputMode="decimal"
                type="number"
                min={0}
                step="any"
                className="input num !text-center !text-3xl !font-semibold !min-h-16 font-display"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0"
              />
              <button
                type="button"
                className="btn-ghost !w-12 !px-0"
                aria-label="Increase by 10 mL"
                onClick={() => setAmount(String((Number.isFinite(amountNum) ? amountNum : 0) + 10))}
              >
                <IconPlus size={18} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {QUICK_ML.map((v) => (
                <button key={v} type="button" className="chip" aria-pressed={amountNum === v} onClick={() => setAmount(String(v))}>
                  {v}
                </button>
              ))}
            </div>
            {Number.isFinite(amountNum) && amountNum > formulaWarningMl && (
              <p className="text-xs text-warn">That’s more than {formulaWarningMl} mL — double-check the amount. You can still save it.</p>
            )}
          </div>
        )}

        {type === 'diaper' && (
          <div className="space-y-2">
            <span className="label" id="status-label">
              Status
            </span>
            <div className="segmented grid-cols-3" role="group" aria-labelledby="status-label">
              {(['wet', 'dirty', 'mixed'] as const).map((s) => (
                <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)} data-autofocus={s === status ? true : undefined}>
                  {s === 'mixed' ? 'Wet + dirty' : s[0].toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}

        <DateTimeField
          label={type === 'sleep' ? 'Fell asleep' : 'Time'}
          value={start}
          onChange={setStart}
          tz={tz}
          id="start-time"
          onNow={() => setStart(dtFromMs(Date.now(), tz))}
        />

        {type === 'sleep' && (
          <>
            {editing && editing.ended_ms === null && (
              <label className="flex min-h-11 items-center gap-3 text-sm">
                <input type="checkbox" checked={running} onChange={(e) => setRunning(e.target.checked)} className="size-5 accent-[var(--color-sleep)]" />
                Still sleeping
              </label>
            )}
            {!running && (
              <DateTimeField
                label="Woke up"
                value={end}
                onChange={setEnd}
                tz={tz}
                id="end-time"
                onNow={() => setEnd(dtFromMs(Date.now(), tz))}
              />
            )}
            {durationMs !== null && (
              <p className={`text-sm ${durationMs < 0 ? 'text-danger' : 'text-ink-300'}`}>
                Duration: <span className="num font-semibold">{durationMs < 0 ? 'invalid' : formatDuration(durationMs)}</span>
              </p>
            )}
            <div className="space-y-2">
              <span className="label" id="kind-label">
                Type (optional)
              </span>
              <div className="segmented grid-cols-3" role="group" aria-labelledby="kind-label">
                <button type="button" aria-pressed={kind === null} onClick={() => setKind(null)}>
                  Unlabelled
                </button>
                <button type="button" aria-pressed={kind === 'nap'} onClick={() => setKind('nap')}>
                  Nap
                </button>
                <button type="button" aria-pressed={kind === 'night'} onClick={() => setKind('night')}>
                  Night
                </button>
              </div>
            </div>
            {overlaps.length > 0 && (
              <div className="rounded-2xl border border-warn/30 bg-warn/10 p-3 text-sm">
                <p className="text-warn">Overlaps {overlaps.length} other sleep session{overlaps.length > 1 ? 's' : ''}:</p>
                <ul className="mt-1 text-ink-300">
                  {overlaps.slice(0, 3).map((o) => (
                    <li key={o.id} className="num">
                      {formatTime(o.started_ms, tz)} – {o.ended_ms ? formatTime(o.ended_ms, tz) : 'running'}
                    </li>
                  ))}
                </ul>
                <label className="mt-2 flex min-h-10 items-center gap-2">
                  <input type="checkbox" checked={overlapAck} onChange={(e) => setOverlapAck(e.target.checked)} />
                  Save anyway
                </label>
              </div>
            )}
          </>
        )}

        <div className="space-y-1.5">
          <label htmlFor="note" className="label">
            Note
          </label>
          <textarea
            id="note"
            rows={2}
            maxLength={2000}
            className="input !min-h-20 py-2"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Optional"
          />
        </div>

        {editing && (
          <p className="flex items-center gap-2 text-xs text-ink-400">
            <SyncBadge state={editing.sync_state} showLabel />
            {editing.payload && 'source' in editing.payload && editing.payload.source && (
              <span>· imported from {editing.payload.source.file} row {editing.payload.source.row}</span>
            )}
          </p>
        )}

        {overlapError && <p role="alert" className="text-danger">Could not check sleep overlaps. Reopen the form to retry.</p>}
        {error && (
          <p role="alert" className="rounded-xl border border-danger/30 bg-danger/10 p-3 text-sm text-ink-100">
            {error}
          </p>
        )}
      </form>
    </Sheet>
  );
}
